/// <reference types="@types/google.maps" />
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdTooltip } from '@commander-services/gui-components';
import { useQueries } from '@tanstack/react-query';
import { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import usePlayer from './hooks/usePlayer';
import usePositionMarker from './hooks/usePositionMarker';
import useRideLine from './hooks/useRideLine';
import useAlarmMarker from './hooks/useAlarmMarker';
import useAlarmLine from './hooks/useAlarmLine';
import useSpeedLimitLine from './hooks/useSpeedLimitLine';
import useEcoDriveLine from './hooks/useEcoDriveLine';
import useEventLine from './hooks/useEventLine';
import useEventMarker from './hooks/useEventMarker';
import {
  ALARM_LINE_COLOR,
  IS_ALARM_LAYER,
  IS_RIDE_LAYER,
  RIDE_LINE_COLOR,
  RIDE_DETAIL_ZOOM_LEVEL,
  IS_ECO_DRIVE_LAYER,
  IS_EVENT_LAYER,
  EVENT_LINE_COLOR,
  LIGHT_GREY_LINE_COLOR,
  DARK_GREY_LINE_COLOR,
  EVENT_ACTIVE_LINE_COLOR,
  PLAYED_LINE_RIDE_LAYER_COLOR,
  PLAYED_LINE_ECODRIVE_LAYER_COLOR,
  PLAYED_LINE_ALARM_LAYER_COLOR,
  PLAYED_LINE_EVENT_LAYER_COLOR,
} from './constants';
import useTimeline from './hooks/useTimeline';
import RidePlayerSpeedMenu from './RidePlayerSpeedMenu';
import ConfirmDialog from '../ConfirmDialog';
import useArrowMarker from './hooks/useArrowMarker';
import useVehicleMarker from './hooks/useVehicleMarker';
import useWaitingMarker from './hooks/useWaitingMarker';
import { showMessage } from '../Toastr/ToastService';
import TimelineLayer, { IDisabledTimelineLayer } from './TimelineLayer';
import SelectRidesModal from './SelectRidesModal';
import useRideNumberMarker from './hooks/useRideNumberMarker';
import PermissionsService, { PERMISSION_RIDE_INFO_EVENT } from '../../Services/PermissionsService';
import { IRide, IRidePlayerSpeedLimitsSettings, IRidePosition } from './types';
import { IVehicle, IVehicleData, IVehicleGdprDataAllowed } from '../Vehicles/types';
import { IS_GPS_RIDE } from '../TripsOverviewNew/constants';
import { vehicleDataByIdSelector } from '../../store/recoil/vehiclesData';
import { isVehicleGdprDataAllowedAtom, vehicleByIdSelector } from '../../store/recoil/vehicles';
import {
  activeRideIndexAtom,
  actualPlayPositionAtom,
  enableArrowsAtom,
  enableFitBoundsAtom,
  enableSpeedlimitsAtom,
  enableWaitingsAtom,
  ignoreRideChangeAtom,
  isRideActiveAtom,
  isRidePlayedAtom,
  playerSpeedAtom,
  reloadLastRideAtom,
  rideAutofocusAtom,
  selectedRidesAtom,
  showAllRidesAtom,
  showChangeRideConfirmDialogAtom,
  showLastRideChangedWarningAtom,
  showSelectRidesModalAtom,
  speedlimitSettingsAtom,
} from './RidePlayerState';
import {
  createActiveRideLine,
  getPathFromRidePositions,
  loadSettings,
  saveSettings,
} from './RidePlayerService';
import { hidePositionMarkersByByArrowMarkers } from './RidePlayerMarkerService';
import { isOnlinePanelOpenAtom } from '../OnlinePanel/OnlinePanelState';
import { isRidePlayerOpenAtom, vehicleFocusAtom } from '../OnlineMapNew/OnlineMapState';
import useAnalytics from '../../hooks/useAnalytics';

interface IRidePlayerProps {
  rides: IRide[];
  googleMap: google.maps.Map;
  actualVehiclePosition?: IRidePosition | null;
  // unmountCallback?: () => void;
  isLastRidePlayer?: boolean;
}

function RidePlayer(props: IRidePlayerProps): JSX.Element {
  const { trackEvent } = useAnalytics();
  const intl = useIntl();
  const { formatMessage: f } = intl;
  const isOnlinePanelOpen = useRecoilValue<number | false>(isOnlinePanelOpenAtom);
  const isVehicleGdprDataAllowed = useRecoilValue<IVehicleGdprDataAllowed>(
    isVehicleGdprDataAllowedAtom
  );
  const [showAllRides, setShowAllRides] = useRecoilState<boolean>(showAllRidesAtom); // When enabled, the handleShowAllRides will be executed and activeRide useEffect will be ignored
  const [activeRideIndex, setActiveRideIndex] = useRecoilState<number>(activeRideIndexAtom);
  const [actualPosition, setActualPosition] = useRecoilState<number>(actualPlayPositionAtom);
  const {
    isOpen,
    pause,
    setPause,
    stop,
    setStop,
    play,
    setPlay,
    next,
    setNext,
    prev,
    setPrev,
    enableSpeedLimits,
    setTime,
    autoplay,
    setAutoplay,
    firstPlay,
    setFirstPlay,
    interval,
    zoomLevel,
    handleToggleSize,
    handleSpeedMenu,
    isSpeedMenuOpen,
    activeLayer,
    handleActiveLayer,
    // rideForVehicle,
    setInterval,
    setIsSpeedMenuOpen,
  } = usePlayer(props.googleMap, props.rides, props.isLastRidePlayer, props.actualVehiclePosition);

  // Custom hook for work with ride position markers
  const {
    positionMarkers,
    showAllPositionMarkers,
    hideAllPositionMarkers,
    setAllPositionMarkersColor,
    resetPositionMarkersColorForRide,
    resetAllPositionMarkersColor,
    setAllStopMarkersDisabled,
    setAllStopMarkersEnabled,
    setStopMarkerEnabled,
    setStopMarkerZIndexForRide,
    resetAllStopMarkersZIndex,
    changeAllPositionMarkersToAlarm,
    changeAllPositionMarkersToEvent,
    changeAllPositionMarkersToEcoDrive,
    changeAllPositionMarkersToRide,
    setAllPositionMarkersZIndex,
    hideAllPositionMarkersExceptRide,
    showAllPositionMarkersForRide,
  } = usePositionMarker(
    props.googleMap,
    props.rides,
    zoomLevel,
    activeLayer,
    props.isLastRidePlayer,
    props.actualVehiclePosition
  );

  // Custom hook for work with arrow markers
  const { arrowMarkers, hideAllArrowMarkers, removeAllArrowMarkers, showArrowMarkersForRide } =
    useArrowMarker(props.googleMap, props.rides, activeLayer, props.isLastRidePlayer);

  // Custom hook for work with alarm markers
  const {
    alarmMarkers,
    alarmPositionMarkerTooltips,
    showAlarms,
    showAllAlarmIconMarkers,
    setAlarmMarkersOpacity,
    setAlarmMarkerOpacity,
    hideAllAlarmIconMarkers,
    setAllAlarmIconMarkersOpacity,
  } = useAlarmMarker(
    props.googleMap,
    props.rides,
    activeLayer,
    zoomLevel,
    props.actualVehiclePosition
  );

  // Custom hook for work with event markers
  const {
    showAllEventIconMarkers,
    hideAllEventIconMarkers,
    setEventIconMarkersOpacity,
    setEventIconMarkerOpacity,
    setAllEventIconMarkersOpacity,
  } = useEventMarker(
    props.googleMap,
    props.rides,
    activeLayer,
    zoomLevel,
    props.actualVehiclePosition
  );

  // Custom hook for work with ride lines
  const {
    playedRideLines,
    setRideLineOpacity,
    setAllRideLinesColor,
    setRideLineColor,
    removePlayedLines,
    showAllRideLines,
    setAllRideLinesZIndex,
    setRideLineZIndex,
    setPlayedRideLineZIndex,
    resetAllPlayedRideLines,
    setPlayedRideLineColor,
    setAllPlayedRideLinesZindex,
  } = useRideLine(props.googleMap, props.rides, activeLayer, props.actualVehiclePosition);

  // Custom hook for work with alarm lines
  const {
    setAlarmLinesOpacity,
    showAllAlarmLines,
    hideAllAlarmLines,
    setAlarmRideLinesSize,
    setAllAlarmLinesColor,
    removeAllAlarmLines,
    createAlarmLines,
    resetAllAlarmLinesColor,
    resetAlarmLinesColorForRide,
    setAllAlarmLinesZIndex,
    setAlarmLineZIndex,
  } = useAlarmLine(props.googleMap, props.rides, activeLayer, props.actualVehiclePosition);

  // Custom hook for working with waiting markers
  const { showAllWaitingMarkers, hideAllWaitingMarkers } = useWaitingMarker(
    props.googleMap,
    props.rides,
    zoomLevel
  );

  const then = useRef<number>(0);

  const dataForVehicle = useRecoilValue<IVehicleData | null>(
    vehicleDataByIdSelector(isOnlinePanelOpen)
  );
  const vehicle = useRecoilValue<IVehicle | null>(vehicleByIdSelector(isOnlinePanelOpen));

  // const [rideChangedWarning, setRideChangedWarning] = React.useState<boolean>(false);
  const [ignoreRideChange, setIgnoreRideChange] = useRecoilState<boolean>(ignoreRideChangeAtom);
  const [showChangeRideConfirmDialog, setShowChangeRideConfirmDialog] = useRecoilState<boolean>(
    showChangeRideConfirmDialogAtom
  );
  const setReloadLastRide = useSetRecoilState<boolean>(reloadLastRideAtom);
  const [enableSpeedlimits, setEnableSpeedlimits] = useRecoilState<boolean>(enableSpeedlimitsAtom);
  const [enableWaitings, setEnableWaitings] = useRecoilState<boolean>(enableWaitingsAtom);
  const [enableArrows, setEnableArrows] = useRecoilState(enableArrowsAtom);
  const enableFitBounds = useRecoilValue<boolean>(enableFitBoundsAtom);
  const [speedlimits, setSpeedLimits] =
    useRecoilState<IRidePlayerSpeedLimitsSettings>(speedlimitSettingsAtom);
  const [playerSpeed, setPlayerSpeed] = useRecoilState<number>(playerSpeedAtom);
  const rideAutofocus = useRecoilValue<boolean>(rideAutofocusAtom);
  const [isRidePlayed, setIsRidePlayed] = useRecoilState<boolean>(isRidePlayedAtom);
  const [settingsLoaded, setSettingsLoaded] = React.useState<boolean>(false);
  // Check if ride is active in multiplayer
  const [isRideActive, setIsRideActive] = useRecoilState<number | false>(isRideActiveAtom);
  const showSelectRidesModal = useRecoilValue<boolean>(showSelectRidesModalAtom);
  const [selectedRides, setSelectedRides] = useRecoilState<number[]>(selectedRidesAtom);
  // const [rideChangedWarning, setRideChangedWarning] = useState<boolean>(false);
  // Check if ride is active in multiplayer
  const isRidePlayerOpen = useRecoilValue<number | boolean>(isRidePlayerOpenAtom);
  const vehicleFocus = useRecoilValue<boolean>(vehicleFocusAtom);
  const [showLastRideChangedWarning, setShowLastRideChangedWarning] = useRecoilState<boolean>(
    showLastRideChangedWarningAtom
  );
  // const setCurrentRidePositionForSelectedVehicle = useSetRecoilState<IRidePosition | null>(
  //   OnlineMapState.currentRidePositionForSelectedVehicle
  // );
  // const [lastRideForSelectedVehicle, setLastRideForSelectedVehicle] = useRecoilState<IRide | null>(
  //   OnlineMapState.lastRideForSelectedVehicle
  // );

  // Custom hook for work with vehicle marker
  const { removeVehicleMarker } = useVehicleMarker(
    props.googleMap,
    props.rides,
    props.isLastRidePlayer,
    vehicle,
    dataForVehicle
  );

  // Custom hook for work with speedlimits lines
  const {
    showAllSpeedLimitLines,
    hideAllSpeedLimitLines,
    setAllSpeedLimitLinesColor,
    createSpeedLimitLinesForRide,
    createSpeedLimitLines,
    removeAllSpeedLimitLines,
    setSpeedLimitLineZIndex,
    setAllSpeedLimitLinesZIndex,
  } = useSpeedLimitLine(props.googleMap, props.rides, activeLayer, props.actualVehiclePosition);

  // Custom hook for work with ecoDrive lines
  const {
    showAllEcoDriveLines,
    hideAllEcoDriveLines,
    setAllEcoDriveLinesColor,
    createEcoDriveLines,
    removeAllEcoDriveLines,
    resetEcoDriveLinesColorForRide,
    resetAllEcoDriveLinesColor,
    setAllEcoDriveLinesZIndex,
    setEcoDriveLineZIndex,
  } = useEcoDriveLine(props.googleMap, props.rides, activeLayer, props.actualVehiclePosition);

  // Custom hook for work with event lines
  const {
    showAllEventLines,
    hideAllEventLines,
    setAllEventLinesColor,
    setEventLinesColorForRide,
    resetAllEventLinesColor,
    setEventLineZIndex,
    setAllEventLinesZIndex,
  } = useEventLine(props.googleMap, props.rides, activeLayer, props.actualVehiclePosition);

  // Custom hook for work with timeline
  const {
    speedLimitLayer,
    alarmLayer,
    ecoDriveLayer,
    eventLayer,
    handleTimelineTrackRef,
    timelineCursorPosition,
    updateTimelinePositionMarker,
    hideTimelinePositionMarker,
    showTimelinePositionMarker,
    showLayerData,
    setShowLayerData,
    timelinePositionMarker,
    timelinePositionMarkerTooltip,
  } = useTimeline(props.googleMap, props.rides, actualPosition, props.isLastRidePlayer);

  // Custom hook for ride number markers
  const {
    setRideNumberMarkerZIndexForRide,
    resetAllRideNumberMarkersZIndex,
    setAllRideNumberMarkerDisabled,
    setAllRideNumberMarkerEnabled,
    setRideNumberMarkerEnabled,
  } = useRideNumberMarker(
    props.googleMap,
    props.rides,
    setIsRideActive,
    playedRideLines,
    timelinePositionMarker,
    timelinePositionMarkerTooltip
  );

  // Handle fitbounds based on desired conditions
  const fitBounds = useCallback(
    (rideNumber?: number) => {
      let canSetBounds = false;
      const mapBounds = new google.maps.LatLngBounds();
      if (rideNumber && selectedRides.includes(rideNumber)) {
        positionMarkers[rideNumber].forEach((marker: google.maps.Marker) => {
          if (marker.getPosition()) {
            mapBounds.extend(marker.getPosition() as google.maps.LatLng);
          }
        });
        if (positionMarkers[rideNumber].length > 1 && !canSetBounds) {
          canSetBounds = true;
        }
      } else {
        Object.keys(positionMarkers).forEach((rideNum: string) => {
          if (selectedRides.includes(Number(rideNum))) {
            if (positionMarkers[Number(rideNum)].length > 1 && !canSetBounds) {
              canSetBounds = true;
            }
            positionMarkers[Number(rideNum)].forEach((marker: google.maps.Marker) => {
              if (marker.getPosition()) {
                mapBounds.extend(marker.getPosition() as google.maps.LatLng);
              }
            });
          }
        });
      }

      if (canSetBounds) {
        props.googleMap.fitBounds(mapBounds, {
          top: 300,
          bottom: 150,
          left: 150,
          right: 150,
        });
      }
    },
    [positionMarkers, props.googleMap, selectedRides]
  );

  const setupShowAllRides = () => {
    setShowAllRides(true); // Set show all rides as active
    setPlay(false);
    setPause(false);
    setStop(false);
    setNext(false);
    setPrev(false);
    setIsRideActive(false);
    setActualPosition(0);
    hideTimelinePositionMarker();
    setShowLayerData(false);
    removePlayedLines();
    setActiveRideIndex(0);
    if (activeLayer === IS_ALARM_LAYER) {
      setAllRideLinesColor(ALARM_LINE_COLOR);
    }
    if (activeLayer === IS_RIDE_LAYER || activeLayer === IS_ECO_DRIVE_LAYER) {
      setAllRideLinesColor(RIDE_LINE_COLOR);
    }
    if (activeLayer === IS_EVENT_LAYER) {
      setAllRideLinesColor(EVENT_LINE_COLOR);
    }
    removeAllSpeedLimitLines();
    createSpeedLimitLines(true);
    removeAllEcoDriveLines();
    createEcoDriveLines();
    removeAllAlarmLines();
    createAlarmLines();
    resetAllEcoDriveLinesColor();
    resetAllEventLinesColor();
    resetAllAlarmLinesColor();
    resetAllPositionMarkersColor();
    setAllEventIconMarkersOpacity(1.0);
    setAllAlarmIconMarkersOpacity(1.0);
    setAllRideLinesZIndex(3); // 1
    setAllPlayedRideLinesZindex(2); // 1
    setAllEventLinesZIndex(3);
    setAllSpeedLimitLinesZIndex(3);
    setAllEcoDriveLinesZIndex(3); // 1
    setAllAlarmLinesZIndex(3); // 1
    resetAllRideNumberMarkersZIndex();
    setAllRideNumberMarkerEnabled();
    setAllStopMarkersEnabled();
    resetAllStopMarkersZIndex();
    setAllPositionMarkersZIndex(98);
    fitBounds();
  };

  const getRideNumberFromSelectedRidesByIndex = useCallback(
    (index: number): number => {
      if (selectedRides[index]) {
        return selectedRides[index];
      }
      return selectedRides[0];
    },
    [selectedRides]
  );

  const handleShowAllRides = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    trackEvent(['Ride player', 'click', 'Show all rides']);
    event.preventDefault();
    setupShowAllRides();
  };

  const showRideAsUnplayed = (ride: IRide) => {
    playedRideLines[ride.rideData.key].setPath([]);
    const firstPosition = ride.positions[0];
    updateTimelinePositionMarker(0, firstPosition, ride.rideData);
  };

  const setupActiveRide = useCallback(() => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    if (enableFitBounds || rideAutofocus) {
      fitBounds(activeRide);
    }
    // Set grey color to all rides, except acive one
    setAllRideLinesColor(LIGHT_GREY_LINE_COLOR, activeRide);
    setAllSpeedLimitLinesColor(DARK_GREY_LINE_COLOR, activeRide);
    setAllPositionMarkersColor(DARK_GREY_LINE_COLOR, activeRide);
    setAllPositionMarkersZIndex(3, activeRide);
    hideAllPositionMarkers(activeRide);
    showAllPositionMarkersForRide(activeRide);
    setAllAlarmLinesColor(DARK_GREY_LINE_COLOR, activeRide);
    setAllAlarmLinesZIndex(1, activeRide); // 3
    setAllRideLinesZIndex(1, activeRide); // 3
    setAllEventLinesZIndex(1, activeRide); // 3
    setAllSpeedLimitLinesZIndex(1, activeRide); // 3
    setAllEcoDriveLinesZIndex(1, activeRide); // 3
    setAllEcoDriveLinesColor(DARK_GREY_LINE_COLOR, activeRide);
    setAllEventLinesColor(DARK_GREY_LINE_COLOR, activeRide);
    // Set all event icon markers opacity for all rides except actual one
    setAllEventIconMarkersOpacity(0.3, activeRide);
    setAllAlarmIconMarkersOpacity(0.3, activeRide);
    let lineColor = RIDE_LINE_COLOR; // For ride and ecoDrive line
    if (activeLayer === IS_ALARM_LAYER) {
      lineColor = ALARM_LINE_COLOR;
    }
    if (activeLayer === IS_EVENT_LAYER) {
      lineColor = EVENT_LINE_COLOR;
    }
    // Set ride color for active ride
    setRideLineColor(activeRide, lineColor);
    // Set normal color for position markers for active ride based on speedlimits or layer
    resetPositionMarkersColorForRide(activeRide);
    // Set normal color for ecodrive lines for active ride
    resetEcoDriveLinesColorForRide(activeRide);
    // Set original color for alarm lines for active ride
    resetAlarmLinesColorForRide(activeRide);
    // Set event lines color for active ride
    setEventLinesColorForRide(activeRide, EVENT_ACTIVE_LINE_COLOR);
    // Set alarm lines zIndex for active ride
    setAlarmLineZIndex(activeRide, 4);
    // Set ride line zIndex for active ride
    setRideLineZIndex(activeRide, 3);
    // Set event lines zIndex for active ride
    setEventLineZIndex(activeRide, 3);
    // Set eco drive line zIndex for active ride
    setEcoDriveLineZIndex(activeRide, 4);
    // Set played ride line zIndex for active ride
    setPlayedRideLineZIndex(activeRide, 2); // 2
    resetAllPlayedRideLines();
    // Set new zIndex to active ride number icon
    resetAllRideNumberMarkersZIndex();
    setRideNumberMarkerZIndexForRide(activeRide, 9999);
    // Set ride number icon marker to enabled
    setRideNumberMarkerEnabled(activeRide);
    // Set all ride number markers disabled (gray color) except active ride
    setAllRideNumberMarkerDisabled(activeRide);
    // Set stop marker enabled for active ride
    setStopMarkerEnabled(activeRide);
    // Set all stop markers disabled (gray color) except active ride
    setAllStopMarkersDisabled(activeRide);
    // Set new zIndex to active ride stop icon
    resetAllStopMarkersZIndex();
    setStopMarkerZIndexForRide(activeRide, 9998);
    // Hide all arrow markers
    hideAllArrowMarkers();
    hideAllAlarmIconMarkers();
    // Show arrow markers for active ride
    if (enableArrows) {
      showArrowMarkersForRide(activeRide);
    }
    // Re-render speedlimit lines for selected ride
    createSpeedLimitLinesForRide(activeRide);
    // Set speedlimit lines zIndex
    setSpeedLimitLineZIndex(activeRide, 5);
    if (prev || next) {
      updateTimelinePositionMarker(
        0,
        props.rides[activeRide - 1].positions[0],
        props.rides[activeRide - 1].rideData
      );
      setActualPosition(0);
    }
    setShowLayerData(true);
    setNext(false);
    setPrev(false);
  }, [
    activeLayer,
    activeRideIndex,
    createSpeedLimitLinesForRide,
    enableArrows,
    enableFitBounds,
    fitBounds,
    getRideNumberFromSelectedRidesByIndex,
    hideAllAlarmIconMarkers,
    hideAllArrowMarkers,
    hideAllPositionMarkers,
    next,
    prev,
    props.rides,
    resetAlarmLinesColorForRide,
    resetAllPlayedRideLines,
    resetAllRideNumberMarkersZIndex,
    resetAllStopMarkersZIndex,
    resetEcoDriveLinesColorForRide,
    resetPositionMarkersColorForRide,
    rideAutofocus,
    setActualPosition,
    setAlarmLineZIndex,
    setAllAlarmIconMarkersOpacity,
    setAllAlarmLinesColor,
    setAllAlarmLinesZIndex,
    setAllEcoDriveLinesColor,
    setAllEcoDriveLinesZIndex,
    setAllEventIconMarkersOpacity,
    setAllEventLinesColor,
    setAllEventLinesZIndex,
    setAllPositionMarkersColor,
    setAllPositionMarkersZIndex,
    setAllRideLinesColor,
    setAllRideLinesZIndex,
    setAllRideNumberMarkerDisabled,
    setAllSpeedLimitLinesColor,
    setAllSpeedLimitLinesZIndex,
    setAllStopMarkersDisabled,
    setEcoDriveLineZIndex,
    setEventLineZIndex,
    setEventLinesColorForRide,
    setNext,
    setPlayedRideLineZIndex,
    setPrev,
    setRideLineColor,
    setRideLineZIndex,
    setRideNumberMarkerEnabled,
    setRideNumberMarkerZIndexForRide,
    setShowLayerData,
    setSpeedLimitLineZIndex,
    setStopMarkerEnabled,
    setStopMarkerZIndexForRide,
    showAllPositionMarkersForRide,
    showArrowMarkersForRide,
    updateTimelinePositionMarker,
  ]);

  // Handle active ride, when active ride changes
  useEffect(() => {
    if (
      // activeRideIndex === typeof 'number' &&
      typeof activeRideIndex === 'number' &&
      !showAllRides &&
      !props.isLastRidePlayer &&
      isRideActive &&
      selectedRides[activeRideIndex]
    ) {
      setIsRideActive(selectedRides[activeRideIndex]);
      setupActiveRide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRideIndex, props.isLastRidePlayer, selectedRides]);

  // Show toast when data flow is not allowed due to GDPR settings
  useEffect(() => {
    if (isOnlinePanelOpen !== false && isVehicleGdprDataAllowed[isOnlinePanelOpen]) {
      if (!isVehicleGdprDataAllowed[isOnlinePanelOpen]) {
        showMessage(
          f({ id: 'toast.ridePlayer.gdprWarning.title' }),
          f({ id: 'toast.ridePlayer.gdprWarning.message' }),
          'warning'
        );
      }
    }
  }, [f, isOnlinePanelOpen, isVehicleGdprDataAllowed]);

  // Save settings when changed
  useEffect(() => {
    if (settingsLoaded) {
      const rideRenderType = enableArrows ? 'line_arrow' : 'line';
      const saveRidePlayerSettings = async () => {
        await saveSettings(
          rideRenderType,
          enableWaitings,
          enableSpeedlimits,
          speedlimits,
          playerSpeed
        );
      };
      saveRidePlayerSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableArrows, enableSpeedlimits, enableWaitings, playerSpeed, speedlimits]);

  const queries = useQueries({
    queries: [
      {
        queryKey: ['playerSettings'],
        queryFn: () => loadSettings(),
        enabled: true,
      },
    ],
  });

  const { data: settingsData } = queries[0];

  // Load user settings
  useEffect(() => {
    if (settingsData && !settingsLoaded) {
      setEnableWaitings(settingsData.showPause);
      setEnableArrows(settingsData.rideRenderType === 'line_arrow');
      setEnableSpeedlimits(settingsData.speedLimits.enabled);
      setSpeedLimits({
        minimumSpeed: settingsData.speedLimits.limits[0],
        warningSpeed: settingsData.speedLimits.limits[1],
        maximumSpeed: settingsData.speedLimits.limits[2],
      });
      setPlayerSpeed(settingsData.playerSpeed);
      setInterval(1500 / settingsData.playerSpeed);
      setSettingsLoaded(true);
    }
  }, [
    setEnableArrows,
    setEnableSpeedlimits,
    setEnableWaitings,
    setInterval,
    setPlayerSpeed,
    setSpeedLimits,
    settingsData,
    settingsLoaded,
  ]);

  // Mount and unmount callback
  useEffect(() => {
    if (props.isLastRidePlayer) {
      setActualPosition(props.rides[0].positions.length - 1);
    } else {
      setActualPosition(0);
    }

    const newSelectedRides: number[] = [];
    props.rides.forEach((ride: IRide) => {
      // If is multi ride player - check if rideData.source is gps ride. I
      // On last ride player - rideData.source not exists
      if (ride.rideData.source === IS_GPS_RIDE || ride.rideData.source === undefined) {
        newSelectedRides.push(ride.rideData.key);
      }
    });
    setSelectedRides(newSelectedRides);

    return () => {
      // if (props.unmountCallback) {
      //   props.unmountCallback();
      // }
      hideAllArrowMarkers();
      removeAllArrowMarkers();
      removeVehicleMarker();
      setActualPosition(0);
      if (ignoreRideChange) {
        setIgnoreRideChange(false);
        setShowChangeRideConfirmDialog(false);
        setShowLastRideChangedWarning(false);
      }
      // console.log('lastRideForSelectedVehicle: ', lastRideForSelectedVehicle);
      // if (lastRideForSelectedVehicle) {
      //   setLastRideForSelectedVehicle(null);
      //   setCurrentRidePositionForSelectedVehicle(null);
      // }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show confirm dialog if last ride changes
  useEffect(() => {
    if (showLastRideChangedWarning && !ignoreRideChange) {
      // if (activeLastRideId !== null && dataForVehicle) {
      //   if (dataForVehicle.lastRide && dataForVehicle.lastRide.id) {
      //     if (
      //       activeLastRideId !== String(dataForVehicle.lastRide.id) &&
      //       !rideChangedWarning &&
      //       // dataForVehicle.vehicleId === rideForVehicle
      //       dataForVehicle.vehicleId === isRidePlayerOpen
      //     ) {
      //       setShowChangeRideConfirmDialog(true);
      //     }
      //   }
      // }
      setShowChangeRideConfirmDialog(true);
      setShowLastRideChangedWarning(false);
    }
    // }, [activeLastRideId, dataForVehicle, isOnlinePanelOpen, rideChangedWarning, rideForVehicle]);
  }, [
    ignoreRideChange,
    setShowChangeRideConfirmDialog,
    setShowLastRideChangedWarning,
    showLastRideChangedWarning,
  ]);

  // Fit bounds for rides
  useEffect(() => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    // if ((enableFitBounds || rideAutofocus) && !isRideActive) {
    //   console.info('bounds 1: ', enableFitBounds, rideAutofocus, isRideActive);
    //   fitBounds();
    // }
    // if ((enableFitBounds || rideAutofocus) && isRideActive) {
    //   console.info('bounds 2: ', enableFitBounds, rideAutofocus, isRideActive);
    //   fitBounds(activeRide);
    // }
    // if (!enableFitBounds && vehicleFocus && !rideAutofocus && isRidePlayerOpen === true) {
    //   console.info('bounds 3: ', enableFitBounds, rideAutofocus, isRideActive, vehicleFocus, isRidePlayerOpen);
    //   fitBounds(activeRide);
    // }

    // If is not last ride player
    if (!props.isLastRidePlayer) {
      if (vehicleFocus && !rideAutofocus && isRidePlayerOpen === true) {
        fitBounds(activeRide);
      }
      if (isRideActive && rideAutofocus) {
        fitBounds(activeRide);
      }
      if (!isRideActive && rideAutofocus) {
        fitBounds();
      }
    }
    // If is last ride player
    if (props.isLastRidePlayer && props.rides) {
      if (isRideActive && rideAutofocus) {
        fitBounds(activeRide);
      }
      if (!isRideActive && rideAutofocus) {
        fitBounds();
      }
    }
  }, [
    activeRideIndex,
    enableFitBounds,
    fitBounds,
    getRideNumberFromSelectedRidesByIndex,
    isRideActive,
    isRidePlayerOpen,
    pause,
    play,
    props.isLastRidePlayer,
    props.rides,
    rideAutofocus,
    showAllRides,
    stop,
    vehicleFocus,
  ]);

  // When new position arrives and rides is not playing - update played ride.
  // useEffect(() => {
  //   if (
  //     props.actualVehiclePosition &&
  //     !play &&
  //     !pause &&
  //     (!stop || stop) &&
  //     !firstPlay &&
  //     !isRidePlayed
  //   ) {
  //     const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
  //     const lastPositionIndex = props.rides[activeRide - 1].positions.length - 1;
  //     const lastPosition = props.rides[activeRide - 1].positions[lastPositionIndex];
  //     if (props.actualVehiclePosition.id === lastPosition.id) {
  //       const lastPositionInPaths = playedRideLines[activeRide].getPath().getArray()[
  //         playedRideLines[activeRide].getPath().getArray().length - 1
  //       ];
  //       if (
  //         lastPositionInPaths &&
  //         lastPositionInPaths.toJSON().lat !== props.actualVehiclePosition.gpsLat &&
  //         lastPositionInPaths.toJSON().lng !== props.actualVehiclePosition.gpsLon
  //       ) {
  //         const playedRideLinePath = [...playedRideLines[activeRide].getPath().getArray()];
  //         playedRideLinePath.push(
  //           new google.maps.LatLng(
  //             props.actualVehiclePosition.gpsLat,
  //             props.actualVehiclePosition.gpsLon
  //           )
  //         );
  //         playedRideLines[activeRide].setPath(playedRideLinePath);
  //       }
  //       updateTimelinePositionMarker(
  //         lastPositionIndex,
  //         props.actualVehiclePosition,
  //         props.rides[activeRide - 1].rideData
  //       );
  //     }
  //   }
  // }, [
  //   activeRideIndex,
  //   playedRideLines,
  //   firstPlay,
  //   isRidePlayed,
  //   pause,
  //   play,
  //   props.actualVehiclePosition,
  //   props.rides,
  //   stop,
  //   updateTimelinePositionMarker,
  //   props.googleMap,
  //   getRideNumberFromSelectedRidesByIndex,
  // ]);
  useEffect(() => {
    if (!props.actualVehiclePosition) return;
    if (play || pause || firstPlay || isRidePlayed) return;

    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const currentRide = props.rides[activeRide - 1];
    const lastPositionIndex = currentRide.positions.length - 1;
    const lastPosition = currentRide.positions[lastPositionIndex];

    if (props.actualVehiclePosition.id !== lastPosition.id) return;

    const currentPathArray = playedRideLines[activeRide].getPath().getArray();
    const lastPositionInPaths = currentPathArray[currentPathArray.length - 1];

    if (
      lastPositionInPaths &&
      (lastPositionInPaths.toJSON().lat !== props.actualVehiclePosition.gpsLat ||
        lastPositionInPaths.toJSON().lng !== props.actualVehiclePosition.gpsLon)
    ) {
      const updatedPath = [
        ...currentPathArray,
        new google.maps.LatLng(
          props.actualVehiclePosition.gpsLat,
          props.actualVehiclePosition.gpsLon
        ),
      ];
      playedRideLines[activeRide].setPath(updatedPath);
    }

    updateTimelinePositionMarker(
      lastPositionIndex,
      props.actualVehiclePosition,
      currentRide.rideData
    );
  }, [
    activeRideIndex,
    playedRideLines,
    firstPlay,
    isRidePlayed,
    pause,
    play,
    props.actualVehiclePosition,
    props.rides,
    stop,
    updateTimelinePositionMarker,
    props.googleMap,
    getRideNumberFromSelectedRidesByIndex,
  ]);

  /** State changes when stop playback occurres */
  const stopPlayback = useCallback(() => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    setPlay(false);
    setPause(false);
    setStop(false);
    setTime(0);
    setIsRidePlayed(false);
    // Set no opacity for displayed ride line, alarm lines and markers of a current ride
    setRideLineOpacity(activeRide, 1.0);
    setAlarmLinesOpacity(activeRide, 1.0);
    setAlarmMarkersOpacity(activeRide, 1.0);
    setEventIconMarkersOpacity(activeRide, 1.0);
    hideTimelinePositionMarker();
  }, [
    activeRideIndex,
    hideTimelinePositionMarker,
    setAlarmLinesOpacity,
    setAlarmMarkersOpacity,
    setEventIconMarkersOpacity,
    setIsRidePlayed,
    setPause,
    setPlay,
    setRideLineOpacity,
    setStop,
    setTime,
    getRideNumberFromSelectedRidesByIndex,
  ]);

  // When selected rides in modal are confirmed, stop playback (playing is paused when modal is open) and setup show all rides mode
  const selectRidesSubmitCallback = () => {
    stopPlayback();
    setupShowAllRides();
  };

  const getPrevSelectedRideIndex = (index: number) => {
    if (selectedRides[index - 1]) {
      return index - 1;
    }
    return selectedRides.length - 1;
  };

  const getNextSelectedRideIndex = useCallback(
    (index: number) => {
      if (selectedRides[index + 1]) {
        return index + 1;
      }
      return 0;
    },
    [selectedRides]
  );

  // Play ride
  useLayoutEffect(() => {
    if (play) {
      let timerId: number;

      const draw = (drawNow: number) => {
        if (!then.current) {
          then.current = drawNow;
        }

        timerId = requestAnimationFrame(draw);

        const delta = drawNow - then.current;

        if (delta > interval) {
          // Just `then = now` is not enough.
          // Lets say we set fps at 10 which means
          // each frame must take 150ms
          // Now frame executes in 16ms (60fps) so
          // the loop iterates 7 times (16*7 = 112ms) until
          // delta > interval === true
          // Eventually this lowers down the FPS as
          // 112*10 = 1120ms (NOT 1000ms).π
          // So we have to get rid of that extra 12ms
          // by subtracting delta (112) % interval (100).
          // Hope that makes sense.

          const activeRideNumber = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
          const rideIndex = activeRideNumber - 1;
          // Ride exists and autoplay is enabled, but is not in selected rides - stop playback
          if (props.rides[rideIndex] && autoplay && !selectedRides.includes(activeRideNumber)) {
            cancelAnimationFrame(timerId);
            stopPlayback();
            setActualPosition(0);
            const nextSelectedRideIndex = getNextSelectedRideIndex(activeRideIndex);
            setActiveRideIndex(nextSelectedRideIndex);
            return;
          }

          // If ride not exists and autoplay is enabled - stop playback
          if (!props.rides[rideIndex] && autoplay) {
            // console.log('Ride not exists and autoplay is enabled - stop playback');
            cancelAnimationFrame(timerId);
            stopPlayback();
            return;
          }
          // If ride line for played line dont exist - create it.
          if (!playedRideLines[activeRideNumber]) {
            // console.log('If ride line for played line dont exist - create it.');
            playedRideLines[activeRideNumber] = createActiveRideLine(props.googleMap);
          }
          // If position for active ride exists and play is enabled
          if (props.rides[rideIndex].positions[actualPosition] && play && !pause) {
            const playedRideLinePath = [...playedRideLines[activeRideNumber].getPath().getArray()];
            playedRideLinePath.push(
              new google.maps.LatLng(
                props.rides[rideIndex].positions[actualPosition].gpsLat,
                props.rides[rideIndex].positions[actualPosition].gpsLon
              )
            );
            playedRideLines[activeRideNumber].setPath(playedRideLinePath);

            if (props.rides[rideIndex].positions[actualPosition + 1]) {
              setActualPosition((oldState: number) => {
                const newActualPosition = oldState + 1;
                // Set timeline marker position
                if (props.rides[rideIndex].positions[newActualPosition]) {
                  updateTimelinePositionMarker(
                    newActualPosition,
                    props.rides[rideIndex].positions[newActualPosition],
                    props.rides[rideIndex].rideData
                  );
                  // Render played polyline via pushing more points to path array
                  const updatedPlayedRideLinePath = [
                    ...playedRideLines[activeRideNumber].getPath().getArray(),
                  ];
                  updatedPlayedRideLinePath.push(
                    new google.maps.LatLng(
                      props.rides[rideIndex].positions[newActualPosition].gpsLat,
                      props.rides[rideIndex].positions[newActualPosition].gpsLon
                    )
                  );
                  playedRideLines[activeRideNumber].setPath(updatedPlayedRideLinePath);
                  // Set event icon marker opacity on actual position
                  setEventIconMarkerOpacity(
                    activeRideNumber,
                    new google.maps.LatLng(
                      props.rides[rideIndex].positions[newActualPosition].gpsLat,
                      props.rides[rideIndex].positions[newActualPosition].gpsLon
                    ),
                    1.0
                  );
                  // Set alarm icon marker opacity on actual position
                  if (showAlarms) {
                    setAlarmMarkerOpacity(
                      activeRideNumber,
                      new google.maps.LatLng(
                        props.rides[rideIndex].positions[newActualPosition].gpsLat,
                        props.rides[rideIndex].positions[newActualPosition].gpsLon
                      ),
                      1.0
                    );
                  }
                  return newActualPosition;
                }
                return oldState;
              });
            }
          }

          // Stop playing on end
          if (
            play &&
            !pause &&
            !autoplay &&
            actualPosition === props.rides[rideIndex].positions.length - 1
          ) {
            cancelAnimationFrame(timerId);
            stopPlayback();
            return;
          }
          if (
            autoplay &&
            props.rides[rideIndex] &&
            actualPosition === props.rides[rideIndex].positions.length - 1
          ) {
            if (selectedRides[activeRideIndex + 1]) {
              setActiveRideIndex(activeRideIndex + 1);
              setActualPosition(0);
            } else {
              cancelAnimationFrame(timerId);

              setPlay(false);
              setPause(false);
              setStop(false);
              setTime(0);
              setIsRidePlayed(false);

              // Set no opacity for displayed ride line, alarm lines and markers of a current ride
              setRideLineOpacity(activeRideNumber, 1.0);
              setAlarmLinesOpacity(activeRideNumber, 1.0);
              setAlarmMarkersOpacity(activeRideNumber, 1.0);
              setEventIconMarkersOpacity(activeRideNumber, 1.0);
              return;
            }
          }

          // Play
          if (play && !pause && props.rides[rideIndex]) {
            then.current = drawNow - (delta % interval);
          }
        }
      };

      timerId = requestAnimationFrame(draw);
      return () => {
        cancelAnimationFrame(timerId);
      };
    }
    return () => {
      return null;
    };
  }, [
    play,
    stop,
    autoplay,
    pause,
    showAlarms,
    enableSpeedLimits,
    interval,
    actualPosition,
    props.rides,
    playedRideLines,
    setActualPosition,
    setPlay,
    setPause,
    setStop,
    setTime,
    setIsRidePlayed,
    setRideLineOpacity,
    setAlarmLinesOpacity,
    setAlarmMarkersOpacity,
    hideTimelinePositionMarker,
    updateTimelinePositionMarker,
    setAlarmMarkerOpacity,
    positionMarkers,
    props.googleMap,
    setEventIconMarkersOpacity,
    setEventIconMarkerOpacity,
    selectedRides,
    stopPlayback,
    getRideNumberFromSelectedRidesByIndex,
    activeRideIndex,
    setActiveRideIndex,
    getNextSelectedRideIndex,
  ]);

  // Handle more details on certain zoom level
  useEffect(() => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    // Handle more details on bigger zoom
    if (zoomLevel >= RIDE_DETAIL_ZOOM_LEVEL) {
      showAllRideLines();
      if (showAllRides) {
        showAllPositionMarkers();
      } else {
        showAllPositionMarkersForRide(activeRide);
      }
      if (activeLayer === IS_ALARM_LAYER) {
        showAllAlarmIconMarkers();
        hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
        hideAllEventIconMarkers();
      }
      if (activeLayer === IS_RIDE_LAYER && enableArrows) {
        hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
      }
      if (activeLayer === IS_ECO_DRIVE_LAYER) {
        hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
      }
      if (activeLayer === IS_EVENT_LAYER) {
        hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
      }
    }
    // Show less details on lower zoom
    if (zoomLevel <= RIDE_DETAIL_ZOOM_LEVEL) {
      showAllRideLines();
      hideAllPositionMarkers();
      if (activeLayer === IS_ALARM_LAYER) {
        hideAllEventIconMarkers();
        showAllAlarmIconMarkers();
        setAlarmLineZIndex(activeRide, 4);
      }
      if (activeLayer === IS_ECO_DRIVE_LAYER) {
        setEcoDriveLineZIndex(activeRide, 4);
      }
    }
  }, [
    activeLayer,
    activeRideIndex,
    arrowMarkers,
    positionMarkers,
    setAlarmRideLinesSize,
    setAllRideLinesColor,
    showAllPositionMarkers,
    zoomLevel,
    showAllEventIconMarkers,
    showAllAlarmIconMarkers,
    hideAllEventIconMarkers,
    hideAllPositionMarkers,
    showAllSpeedLimitLines,
    showAllRideLines,
    setEcoDriveLineZIndex,
    setAlarmLineZIndex,
    hideAllPositionMarkersExceptRide,
    showAllRides,
    showAllPositionMarkersForRide,
    getRideNumberFromSelectedRidesByIndex,
    enableArrows,
  ]);

  // Remove position and alarm markers from map
  useEffect(
    () => () => {
      // Remove position markers from map
      Object.keys(positionMarkers).forEach((rideNumber: string) => {
        positionMarkers[Number(rideNumber)].forEach((marker: google.maps.Marker) => {
          if (marker.getMap()) {
            marker.setMap(null);
          }
        });
        positionMarkers[Number(rideNumber)] = [];
      });

      // Remove alarm markers and infowindows from map
      Object.keys(alarmMarkers).forEach((rideNumber: string) => {
        Object.keys(alarmMarkers[Number(rideNumber)]).forEach((positionIndex: string) => {
          const alarmMarkerData = alarmMarkers[Number(rideNumber)][Number(positionIndex)];
          if (alarmMarkerData.marker && alarmMarkerData.marker.getMap()) {
            alarmMarkerData.marker.setMap(null);
          }
        });
        alarmMarkers[Number(rideNumber)] = [];
        alarmPositionMarkerTooltips[Number(rideNumber)].forEach(
          (infoWindow: google.maps.OverlayView | null) => infoWindow && infoWindow.setMap(null)
        );
        alarmPositionMarkerTooltips[Number(rideNumber)] = [];
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Hides (removes) ride polyline from map
   */
  const hideActiveRideLine = (): void => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    playedRideLines[activeRide].setPath([]);
  };

  /**
   * Show ride as played
   * @param {IRide} ride
   * @returns {void}
   */
  const showRideAsPlayed = (ride: IRide): void => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    playedRideLines[activeRide].setPath(getPathFromRidePositions(ride));
    const lastPosition = ride.positions[ride.positions.length - 1];
    updateTimelinePositionMarker(ride.positions.length - 1, lastPosition, ride.rideData);
  };

  /**
   * Handle next ride
   * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} event
   * @returns {void}
   */
  const handleNextRide = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    trackEvent(['Ride player', 'click', 'Next ride']);
    event.preventDefault();
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const nextActiveRideIndex = getNextSelectedRideIndex(activeRideIndex);
    setIsRidePlayed(false);
    if (showAllRides) {
      setShowAllRides(false);
      setActiveRideIndex(activeRideIndex);
      setIsRideActive(activeRide);
      setShowLayerData(true);
      setupActiveRide();
      return;
    }
    setIsRideActive(activeRide);
    if (!showLayerData && !props.isLastRidePlayer) {
      setShowLayerData(true);
    }

    // If only one ride is provided, show ride as played
    if (selectedRides.length === 1) {
      setPlay(false);
      setStop(true);
      setPause(false);
      setFirstPlay(false);
      const newActualPosition = props.rides[activeRide - 1].positions.length - 1;
      setActualPosition(newActualPosition);
      showRideAsPlayed(props.rides[activeRide - 1]);
    }

    // If more rides is provided
    if (selectedRides.length > 1) {
      setNext(true);
      setActualPosition(0);
      setActiveRideIndex(nextActiveRideIndex);
    }
  };

  const handlePreviousRide = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['Ride player', 'click', 'Previous ride']);
    setIsRidePlayed(true);
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const prevActiveRideIndex = getPrevSelectedRideIndex(activeRideIndex);
    const prevActiveRide = getRideNumberFromSelectedRidesByIndex(prevActiveRideIndex);

    if (showAllRides) {
      setShowAllRides(false);
      setActiveRideIndex(prevActiveRideIndex);
      setIsRideActive(prevActiveRide);
      setShowLayerData(true);
      setupActiveRide();
      return;
    }

    // If only one ride is provided, show ride as unplayed
    if (selectedRides.length === 1) {
      showRideAsUnplayed(props.rides[0]);
      setFirstPlay(true);
      setActualPosition(0);
    }

    // If more than one ride is provided. Hide all bellow this one.
    if (activeRide > 1 && props.rides.length > 1) {
      setPrev(true);
      setIsRideActive(prevActiveRide);
    }

    // If we are on first ride and more rides are provides, jump to last ride
    if (activeRide === 1 && props.rides.length > 1) {
      setPrev(true);
      setIsRideActive(prevActiveRide);
    }

    if (activeRide > 0) {
      setActualPosition(0);
      setActiveRideIndex(prevActiveRideIndex);
    }
  };

  const handlePause = () => {
    trackEvent(['Ride player', 'click', 'Pause']);
    setPause(true);
  };

  const handlePlay = () => {
    trackEvent(['Ride player', 'click', 'Play']);
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    setIsRidePlayed(true);
    setShowAllRides(false);
    // setIsRideActive(activeRide);
    showTimelinePositionMarker();
    if (!showLayerData && !props.isLastRidePlayer) {
      setShowLayerData(true);
    }

    // The ride did not play before or played to end and we want to play it again
    if (firstPlay || (!play && !stop && !pause && !isRidePlayed && !firstPlay)) {
      hideActiveRideLine();
      setActualPosition(0);
      updateTimelinePositionMarker(
        actualPosition,
        props.rides[activeRide - 1].positions[actualPosition],
        props.rides[activeRide - 1].rideData
      );
    }

    if (stop) {
      props.rides.forEach((_element: IRide, _index: number) => {
        hideActiveRideLine();
      });
      setActualPosition(0);
      // Set higher opacity for displayed ride line, alarm lines and markers of a current ride
      setRideLineOpacity(activeRide, 1.0);
      setAlarmLinesOpacity(activeRide, 1.0);
      setAlarmMarkersOpacity(activeRide, 1.0);
      setEventIconMarkersOpacity(activeRide, 1.0);
    }
    setFirstPlay(false);
    if (!play && !pause && stop && !firstPlay) {
      setPause(true);
      setPlay(false);
      setStop(false);
      hideActiveRideLine();
      setActualPosition(0);
      updateTimelinePositionMarker(
        actualPosition,
        props.rides[activeRide - 1].positions[actualPosition],
        props.rides[activeRide - 1].rideData
      );
    }
    if (play && !pause) {
      setPause(true);
      setPlay(false);
      setStop(false);
    } else {
      // Set higher opacity for displayed ride line, alarm lines and markers of a current ride
      setAlarmMarkersOpacity(activeRide, 0.3);
      setEventIconMarkersOpacity(activeRide, 0.3);
      setPlay(true);
      setPause(false);
      setStop(false);
      // setIsRidePlayed(false);
    }
  };

  const handlePlayPause = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (!play && !stop && !pause) {
      setIsRideActive(1);
      handlePlay();
      setupActiveRide();
    }
    if (play && pause && !stop) {
      handlePlay();
    }
    if (play && !pause && !stop) {
      handlePause();
    }
    if (!play && !pause && stop) {
      setActiveRideIndex(0);
      setActualPosition(0);
      setIsRideActive(1);
      removePlayedLines();
      handlePlay();
    }
  };

  const handleCancelReloadRide = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent
  ): void => {
    event.preventDefault();
    trackEvent(['Ride player', 'click', 'Cancel reload ride']);
    setShowChangeRideConfirmDialog(false);
    // setRideChangedWarning(true);
    setIgnoreRideChange(true);
  };

  const handleReloadRide = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.preventDefault();
    trackEvent(['Ride player', 'click', 'Reload ride']);
    setReloadLastRide(true);
    setIgnoreRideChange(false);
    setShowChangeRideConfirmDialog(false);
  };

  /**
   * Handle select ride layer
   * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} event
   * @returns {void}
   */
  const handleSelectRideLayer = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ): void => {
    trackEvent(['Ride player', 'click', 'Select ride layer']);
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    if (activeLayer === IS_RIDE_LAYER) {
      return;
    }
    handleActiveLayer(IS_RIDE_LAYER);
    changeAllPositionMarkersToRide();
    if (enableSpeedlimits) {
      showAllSpeedLimitLines();
    } else {
      hideAllSpeedLimitLines();
    }
    if (isRideActive) {
      setAllRideLinesColor(LIGHT_GREY_LINE_COLOR, activeRide);
      setRideLineColor(activeRide, RIDE_LINE_COLOR);
      setAllPositionMarkersColor(DARK_GREY_LINE_COLOR, activeRide);
    } else {
      setAllRideLinesColor(RIDE_LINE_COLOR);
    }
    if (enableWaitings) {
      showAllWaitingMarkers();
    } else {
      hideAllWaitingMarkers();
    }
    hideAllAlarmLines();
    hideAllAlarmIconMarkers();
    // Hide ecoDriver layer
    hideAllEcoDriveLines();
    // Hide event layer
    hideAllEventLines();
    hideAllEventIconMarkers();

    setPlayedRideLineColor(PLAYED_LINE_RIDE_LAYER_COLOR);

    hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
  };

  /**
   * Handle select alarm layer
   * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} event
   * @returns {void}
   */
  const handleSelectAlarmLayer = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ): void => {
    trackEvent(['Ride player', 'click', 'Select alarm layer']);
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    if (activeLayer === IS_ALARM_LAYER) {
      return;
    }
    handleActiveLayer(IS_ALARM_LAYER);
    changeAllPositionMarkersToAlarm();
    showAllAlarmIconMarkers();
    showAllAlarmLines();
    if (isRideActive) {
      setAllRideLinesColor(LIGHT_GREY_LINE_COLOR, activeRide);
      setAllAlarmLinesColor(DARK_GREY_LINE_COLOR, activeRide);
      setRideLineColor(activeRide, ALARM_LINE_COLOR);
      setAllPositionMarkersColor(DARK_GREY_LINE_COLOR, activeRide);
      setAllAlarmLinesZIndex(2, activeRide);
    } else {
      setAllRideLinesColor(ALARM_LINE_COLOR);
      setAllAlarmLinesZIndex(5);
    }
    // Hide ride layer
    hideAllSpeedLimitLines();
    hideAllWaitingMarkers();
    // Hide ecoDriver layer
    hideAllEcoDriveLines();
    // Hide event layer
    hideAllEventLines();
    hideAllEventIconMarkers();

    setPlayedRideLineColor(PLAYED_LINE_ALARM_LAYER_COLOR);

    hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
  };

  /**
   * Handle select eco drive layer
   * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} event
   * @returns {void}
   */
  const handleSelectEcoDriveLayer = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ): void => {
    trackEvent(['Ride player', 'click', 'Select ecodrive layer']);
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    if (activeLayer === IS_ECO_DRIVE_LAYER) {
      return;
    }
    changeAllPositionMarkersToEcoDrive();
    handleActiveLayer(IS_ECO_DRIVE_LAYER);
    showAllEcoDriveLines();
    setAllEcoDriveLinesZIndex(4);
    if (isRideActive) {
      setAllRideLinesColor(LIGHT_GREY_LINE_COLOR, activeRide);
      setAllEcoDriveLinesColor(DARK_GREY_LINE_COLOR, activeRide);
      setRideLineColor(activeRide, RIDE_LINE_COLOR);
      setAllPositionMarkersColor(DARK_GREY_LINE_COLOR, activeRide);
      setAllEcoDriveLinesZIndex(2, activeRide);
    } else {
      setAllRideLinesColor(RIDE_LINE_COLOR);
      setAllEcoDriveLinesZIndex(5);
    }
    // Hide ride layer
    hideAllSpeedLimitLines();
    hideAllWaitingMarkers();
    // Hide alarm layer
    hideAllAlarmIconMarkers();
    hideAllAlarmLines();
    // Hide event layer
    hideAllEventLines();
    hideAllEventIconMarkers();

    setPlayedRideLineColor(PLAYED_LINE_ECODRIVE_LAYER_COLOR);

    hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
  };

  /**
   * Handle select event layer
   * @param {React.MouseEvent<HTMLDivElement, MouseEvent>} event
   * @returns {void}
   */
  const handleSelectEventLayer = (
    _event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>
  ): void => {
    trackEvent(['Ride player', 'click', 'Select event layer']);
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    if (activeLayer === IS_EVENT_LAYER) {
      return;
    }
    handleActiveLayer(IS_EVENT_LAYER);
    changeAllPositionMarkersToEvent();
    showAllEventLines();
    if (isRideActive) {
      setAllRideLinesColor(LIGHT_GREY_LINE_COLOR, activeRide);
      setAllEventLinesColor(DARK_GREY_LINE_COLOR, activeRide);
      setRideLineColor(activeRide, EVENT_LINE_COLOR);
      setAllPositionMarkersColor(DARK_GREY_LINE_COLOR, activeRide);
    } else {
      setAllRideLinesColor(EVENT_LINE_COLOR);
    }
    // Hide ride layer
    hideAllSpeedLimitLines();
    hideAllWaitingMarkers();
    // Hide alarm layer
    hideAllAlarmIconMarkers();
    hideAllAlarmLines();
    // Hide ecoDrive layer
    hideAllEcoDriveLines();

    setPlayedRideLineColor(PLAYED_LINE_EVENT_LAYER_COLOR);

    hidePositionMarkersByByArrowMarkers(arrowMarkers, positionMarkers, activeRide);
  };

  const handleAutoplay = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (autoplay) {
      trackEvent(['Ride player', 'click', 'Autoplay off']);
      setAutoplay(false);
    } else {
      trackEvent(['Ride player', 'click', 'Autoplay on']);
      setAutoplay(true);
    }
  };

  const isAlarmLayerDisabled = (): { permissions: boolean; gdpr: boolean } => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const activeRideData = props.rides[activeRide - 1];
    const data = {
      permissions: false,
      gdpr: false,
    };

    if (!activeRideData) {
      return data;
    }
    if (props.isLastRidePlayer) {
      return data;
    }
    if (activeRideData.rideData.info?.alarm === '-') {
      return {
        ...data,
        gdpr: true,
      };
    }

    return data;
  };

  const isEventLayerDisabled = (): { permissions: boolean; gdpr: boolean } => {
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const activeRideData = props.rides[activeRide - 1];
    const data = {
      permissions: false,
      gdpr: false,
    };

    if (!activeRideData) {
      return data;
    }
    if (props.isLastRidePlayer) {
      return data;
    }
    if (activeRideData.rideData.info?.event === '-') {
      return {
        ...data,
        gdpr: true,
      };
    }
    if (!PermissionsService.hasAccess(PERMISSION_RIDE_INFO_EVENT, ['read'])) {
      return {
        ...data,
        permissions: true,
      };
    }

    return data;
  };

  const isEcoDriveLayerDisabled = (): IDisabledTimelineLayer => {
    const data: IDisabledTimelineLayer = {
      permissions: false,
      gdpr: false,
    };
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const activeRideData = props.rides[activeRide - 1];
    if (!activeRideData) {
      return data;
    }
    if (props.isLastRidePlayer && !activeRideData.rideData.hasEcoScore) {
      return {
        ...data,
        permissions: true,
      };
    }
    if (activeRideData.rideData && activeRideData.rideData.hasEcoScore) {
      return data;
    }
    if (activeRideData.rideData && !activeRideData.rideData.hasEcoScore) {
      let ecoScoreDisabled = true;
      activeRideData.positions.forEach((position: IRidePosition) => {
        if (position.ecoScore !== null) {
          ecoScoreDisabled = false;
        }
      });
      return {
        ...data,
        permissions: ecoScoreDisabled,
      };
    }
    if (activeRideData.rideData) {
      if (activeRideData.rideData.hasEcoScore) {
        return data;
      }
    }
    return data;
  };

  const getAlarmsLayerTitle = (): string => {
    const disabledData = isAlarmLayerDisabled();
    if (disabledData.permissions && !disabledData.gdpr) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noPermission' });
    }
    if (disabledData.gdpr) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noGdrp' });
    }
    return f({ id: 'ridePlayer.layer.alarms.title' });
  };

  const getEventsLayerTitle = (): string => {
    const disabledData = isEventLayerDisabled();
    if (disabledData.permissions && !disabledData.gdpr) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noPermission' });
    }
    if (disabledData.gdpr) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noGdrp' });
    }
    return f({ id: 'ridePlayer.layer.events.title' });
  };

  const getEcoDriveLayerTitle = (): string => {
    const disabledData = isEcoDriveLayerDisabled();
    const activeRide = getRideNumberFromSelectedRidesByIndex(activeRideIndex);
    const activeRideData = props.rides[activeRide - 1];
    if (
      activeRideData &&
      (activeRideData.rideData.hasEcoScore === false ||
        activeRideData.rideData.hasEcoScore === null)
    ) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noData' });
    }
    if (disabledData.permissions && !disabledData.gdpr) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noPermission' });
    }
    if (disabledData.gdpr) {
      return f({ id: 'ridePlayer.layer.ecoScore.title.noGdrp' });
    }
    return f({ id: 'ridePlayer.layer.ecoScore.title' });
  };

  return (
    <div>
      {showSelectRidesModal && (
        <SelectRidesModal rides={props.rides} submitCallback={selectRidesSubmitCallback} />
      )}
      {showChangeRideConfirmDialog && (
        <ConfirmDialog
          title={f({ id: 'ridePlayer.rideChange.confirmDialog.title' })}
          cancelCallback={handleCancelReloadRide}
          continueCallback={handleReloadRide}
          cancelButtonTitle={f({ id: 'ridePlayer.rideChange.confirmDialog.button.no' })}
          continueButtonTitle={f({ id: 'ridePlayer.rideChange.confirmDialog.button.yes' })}
        >
          {f({ id: 'ridePlayer.rideChange.confirmDialog.message' })}
        </ConfirmDialog>
      )}
      <div className="w-player">
        <div className="b-player-controls">
          <div className="row mb-2">
            <div className="col-auto mx-auto position-relative">
              <CmdTooltip
                customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                content={f({ id: 'ridePlayer.button.playbackSpeedSettings.tooltip' })}
                isTop
                width={200}
              >
                <a
                  href="#"
                  className="action cmd-icons cmd-icon-play-speed"
                  onClick={handleSpeedMenu}
                >
                  {/* */}
                </a>
              </CmdTooltip>

              <div className="play-speed-status">x{playerSpeed}</div>

              {isSpeedMenuOpen && (
                <RidePlayerSpeedMenu closeCallback={() => setIsSpeedMenuOpen(false)} />
              )}
            </div>
          </div>
          <div className="row no-gutters align-items-center">
            <div className="col-auto mr-auto">
              <CmdTooltip
                customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                content={
                  props.rides.length > 1
                    ? f({ id: 'ridePlayer.button.previous.moreRides.toolitp' })
                    : f({ id: 'ridePlayer.button.previous.oneRide.toolitp' })
                }
                width={120}
              >
                <a
                  href="#"
                  className="action cmd-icons cmd-icon-skip-back"
                  onClick={handlePreviousRide}
                >
                  {/* */}
                </a>
              </CmdTooltip>
            </div>
            <div className="col-auto">
              <a
                href="#"
                className={`play ${play && !pause ? 'active' : ''}`}
                onClick={handlePlayPause}
              >
                {!play && !stop && !pause && (
                  <CmdTooltip
                    customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    content={f({ id: 'ridePlayer.button.play.tooltip' })}
                    width={150}
                  >
                    <i className="action action-play cmd-icons cmd-icon-play-simple fs-7" />
                  </CmdTooltip>
                )}
                {play && pause && !stop && (
                  <CmdTooltip
                    customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    content={f({ id: 'ridePlayer.button.play.tooltip' })}
                    width={150}
                  >
                    <i className="action action-play cmd-icons cmd-icon-play-simple fs-7" />
                  </CmdTooltip>
                )}
                {play && !pause && !stop && (
                  <CmdTooltip
                    customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    content={f({ id: 'ridePlayer.button.pause.toolitp' })}
                    width={150}
                  >
                    <i className="action action-play cmd-icons cmd-icon-pause fs-7" />
                  </CmdTooltip>
                )}
                {!play && pause && !stop && (
                  <CmdTooltip
                    customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    content={f({ id: 'ridePlayer.button.play.tooltip' })}
                    width={150}
                  >
                    <i className="action action-play cmd-icons cmd-icon-play-simple fs-7" />
                  </CmdTooltip>
                )}
                {!play && stop && !pause && (
                  <CmdTooltip
                    customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    content={f({ id: 'ridePlayer.button.play.tooltip' })}
                    width={150}
                  >
                    <i className="action action-play cmd-icons cmd-icon-play-simple fs-7" />
                  </CmdTooltip>
                )}
                {!play && stop && pause && (
                  <CmdTooltip
                    customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                    content={f({ id: 'ridePlayer.button.play.tooltip' })}
                    width={150}
                  >
                    <i className="action action-play cmd-icons cmd-icon-play-simple fs-7" />
                  </CmdTooltip>
                )}
              </a>
            </div>
            <div className="col-auto ml-auto">
              <CmdTooltip
                customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                content={
                  props.rides.length > 1
                    ? f({ id: 'ridePlayer.button.next.moreRides.toolitp' })
                    : f({ id: 'ridePlayer.button.next.oneRide.tooltip' })
                }
                width={120}
              >
                <a
                  href="#"
                  className="action cmd-icons cmd-icon-skip-next"
                  onClick={handleNextRide}
                >
                  {/* {} */}
                </a>
              </CmdTooltip>
            </div>
          </div>
          <div className="row mt-2">
            {!props.isLastRidePlayer && props.rides.length > 1 && (
              <div className="col-auto mx-auto player-bottom-actions">
                <CmdTooltip
                  customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  content={f({ id: 'ridePlayer.button.showAll.tooltip' })}
                  width={200}
                  isTop
                >
                  <button
                    type="button"
                    className={`action ${
                      !showLayerData ? 'light' : ''
                    } cmd-icons cmd-icon-rewind-played mx-2`}
                    onClick={handleShowAllRides}
                  >
                    <span className="sr-only">
                      {f({ id: 'ridePlayer.button.showAll.tooltip' })}
                    </span>
                  </button>
                </CmdTooltip>
                <CmdTooltip
                  customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  content={f({ id: 'ridePlayer.button.playAll.tooltip' })}
                  width={200}
                  isTop
                >
                  <button
                    type="button"
                    className={`action ${autoplay ? 'green' : ''} cmd-icons cmd-icon-play-all mx-2`}
                    onClick={handleAutoplay}
                  >
                    <span className="sr-only">
                      {f({ id: 'ridePlayer.button.playAll.tooltip' })}
                    </span>
                  </button>
                </CmdTooltip>
              </div>
            )}
            {/* <div className="col-auto mx-auto">
              <span
                title={f({ id: 'ridePlayer.button.playAll.tooltip' })}
                className="action light cmd-icons cmd-icon-play-all"
              />
            </div> */}
          </div>
        </div>

        <div className={`b-player-status ${isOpen ? 'active' : ''}`}>
          <div className="b-player-status-content d-none d-md-block" style={{ height: '80px' }}>
            <TimelineLayer
              layer={IS_RIDE_LAYER}
              activeLayer={activeLayer}
              selectCallback={handleSelectRideLayer}
              showLayerData={showLayerData}
              data={speedLimitLayer}
              handleTimelineTrackRef={handleTimelineTrackRef}
              enableSpeedlimits={enableSpeedlimits}
              title={f({ id: 'ridePlayer.layer.rides.tooltip' })}
              color="green"
            />
            <TimelineLayer
              layer={IS_ECO_DRIVE_LAYER}
              activeLayer={activeLayer}
              selectCallback={handleSelectEcoDriveLayer}
              showLayerData={showLayerData}
              isDisabled={isEcoDriveLayerDisabled()}
              data={ecoDriveLayer}
              title={getEcoDriveLayerTitle()}
              icon={<i className="cmd-icons cmd-icon-eco-drive" />}
              color="green"
            />
            <TimelineLayer
              layer={IS_ALARM_LAYER}
              activeLayer={activeLayer}
              selectCallback={handleSelectAlarmLayer}
              showLayerData={showLayerData}
              data={alarmLayer}
              // title={f({ id: 'ridePlayer.layer.alarms.title' })}
              isDisabled={isAlarmLayerDisabled()}
              title={getAlarmsLayerTitle()}
              color="red"
              icon={<i className="cmd-icons cmd-icon-alarm-2" />}
              tooltipPlacement="top"
            />
            <TimelineLayer
              layer={IS_EVENT_LAYER}
              activeLayer={activeLayer}
              selectCallback={handleSelectEventLayer}
              showLayerData={showLayerData}
              data={eventLayer}
              isDisabled={isEventLayerDisabled()}
              title={getEventsLayerTitle()}
              color="blue"
              icon={<i className="cmd-icons cmd-icon-events-2" />}
              tooltipPlacement="top"
            />
            {showLayerData && (
              <div
                className="timeline"
                style={{ transform: `translateX(${timelineCursorPosition + 14}px)` }}
              />
            )}
          </div>

          <div className="b-player-status-content b-player-status-content--mobile d-md-none">
            <div className="my-2 ml-2">
              <div className="e-radio">
                <CmdTooltip
                  customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  content={f({ id: 'ridePlayer.layer.speedlimits' })}
                  width={200}
                >
                  <label>
                    <input
                      type="radio"
                      name="set-player-type"
                      checked={activeLayer === IS_RIDE_LAYER}
                      onChange={handleSelectRideLayer}
                    />
                    <span className="fs-1">{f({ id: 'ridePlayer.layer.speedlimits' })}</span>
                  </label>
                </CmdTooltip>
              </div>
            </div>
            <div className="my-2 ml-2">
              <div className="e-radio">
                <CmdTooltip
                  customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  content={f({ id: 'ridePlayer.layer.ecoScore' })}
                  width={200}
                >
                  <label>
                    <input
                      type="radio"
                      name="set-player-type"
                      checked={activeLayer === IS_ECO_DRIVE_LAYER}
                      onChange={handleSelectEcoDriveLayer}
                    />
                    <span className="fs-1" title={f({ id: 'ridePlayer.layer.ecoScore' })}>
                      {f({ id: 'ridePlayer.layer.ecoScore' })}
                    </span>
                  </label>
                </CmdTooltip>
              </div>
            </div>
            <div className="my-2 ml-2">
              <div className="e-radio">
                <CmdTooltip
                  customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  content={f({ id: 'ridePlayer.layer.alarms' })}
                  width={200}
                  isTop
                >
                  <label>
                    <input
                      type="radio"
                      name="set-player-type"
                      checked={activeLayer === IS_ALARM_LAYER}
                      onChange={handleSelectAlarmLayer}
                    />
                    <span className="fs-1" title={f({ id: 'ridePlayer.layer.alarms' })}>
                      {f({ id: 'ridePlayer.layer.alarms' })}
                    </span>
                  </label>
                </CmdTooltip>
              </div>
            </div>
            <div className="my-2 ml-2">
              <div className="e-radio">
                <CmdTooltip
                  customCss={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                  content={f({ id: 'ridePlayer.layer.events' })}
                  width={200}
                  isTop
                >
                  <label>
                    <input
                      type="radio"
                      name="set-player-type"
                      checked={activeLayer === IS_EVENT_LAYER}
                      onChange={handleSelectEventLayer}
                    />
                    <span className="fs-1" title={f({ id: 'ridePlayer.layer.events' })}>
                      {f({ id: 'ridePlayer.layer.events' })}
                    </span>
                  </label>
                </CmdTooltip>
              </div>
            </div>
          </div>
        </div>
        <CmdTooltip
          customCss={{ whiteSpace: 'nowrap', textAlign: 'center', top: '45px' }}
          content={
            isOpen
              ? f({ id: 'ridePlayer.toogleSize.small.tooltip' })
              : f({ id: 'ridePlayer.toogleSize.big.tooltip' })
          }
          width={150}
        >
          <a
            href="#"
            className={`b-player-extend ${isOpen ? 'active' : ''}`}
            onClick={handleToggleSize}
          >
            {/* {} */}
          </a>
        </CmdTooltip>
      </div>
    </div>
  );
}

export default memo(RidePlayer);
